import React, { useState } from 'react';
import { Course } from '../types';
import { formatCurrency } from '../utils/formatUtils';
import { generateCourseAffiliateLink, generateAffiliateText } from '../utils/formatUtils';
import { shouldShowIOSWebViewAlert } from '../utils/deviceDetection';

import { useShare } from '../hooks/useShare';
import { useAuth } from '../contexts/AuthContext';
import { useEnrolledCourses } from '../hooks/useEnrolledCourses';
import { useCourseProgress } from '../hooks/useCourseProgress';
import { useUsers } from '../hooks/useUsers';
import UserProfileLink from './UserProfileLink';
import {
  AcademicCapIcon,
  UserIcon,
  ShareIcon,
  ClockIcon,
  BookOpenIcon,
  ShoppingCartIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  PencilIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

interface CatalogCourseCardProps {
  course: Course;
  onClick: () => void;
  onAddToCart?: (course: Course) => void;
  showAddToCart?: boolean;
  onEdit?: () => void;
  showEditButton?: boolean;
}

const CatalogCourseCard: React.FC<CatalogCourseCardProps> = ({
  course,
  onClick,
  onAddToCart,
  showAddToCart = true,
  onEdit,
  showEditButton = false,
}) => {
  const { handleShare } = useShare();
  const { currentUser } = useAuth();
  const { fetchUsers } = useUsers();
  const [showFullImage, setShowFullImage] = useState(false);
  const [showHelperText, setShowHelperText] = useState(false);
  
  // Fetch course author data when course changes
  React.useEffect(() => {
    if (course?.author?.id) {
      fetchUsers([course.author.id]);
    }
  }, [course?.author?.id, fetchUsers]);
  const classroomsCount = Array.isArray((course as any).classrooms)
    ? ((course as any).classrooms as any[]).length
    : null;

  // Check if user is enrolled in this course
  const { enrolledCourses } = useEnrolledCourses(currentUser?.uid);
  const isEnrolled = enrolledCourses.some(enrolled => enrolled.course_id === course.id);
  
  // Get course progress if enrolled
  const { courseProgress } = useCourseProgress(isEnrolled ? course.id : '');
  const hasProgress = courseProgress && (courseProgress.lastLessonId || courseProgress.completed);
  const isFreeCourse = (course.price_status === 'Free') || (course.pricee === '0') || (Number(course.pricee || '0') === 0);



  const handleShareClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const courseUrl = generateCourseAffiliateLink(course.id, currentUser?.referralCode || "");
    const shareText = generateAffiliateText(course.name, currentUser?.referralCode || "");
    await handleShare({
      title: course.name,
      text: shareText,
      url: courseUrl,
    });
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(course);
    }
  };

  return (
    <div
      onClick={onClick}
      className="group relative bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-200 overflow-hidden"
    >
      <div className="flex flex-col">
        {/* Course Image - Dynamic height based on content */}
        <div className="relative w-full bg-gray-100 overflow-hidden">
          {course.image_url ? (
            <>
              <img
                src={course.image_url}
                alt={course.name}
                className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-500 bg-gray-50"
              />
              {/* Full Image Overlay on Hover */}
              <div className="absolute inset-0 opacity-0 transition-opacity duration-300 z-50 pointer-events-none">
                <div className="absolute top-0 left-full ml-2 bg-white rounded-lg shadow-xl border border-gray-200 p-2 min-w-[300px] max-w-[400px]">
                  <img
                    src={course.image_url}
                    alt={course.name}
                    className="w-full h-auto max-h-[300px] object-contain rounded-lg bg-gray-50"
                  />
                  <p className="text-sm font-medium text-gray-900 mt-2 px-1">
                    {course.name}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="w-full h-64 flex items-center justify-center bg-gray-100">
              <AcademicCapIcon className="h-18 w-18 text-gray-400" />
            </div>
          )}

          {/* Rating Badge */}
          {course.rating > 0 && (
            <div className="absolute top-2 right-2">
              <div className="bg-white px-2 py-1 rounded text-gray-800 flex items-center shadow-sm">
                <StarIconSolid className="h-3 w-3 mr-1 text-yellow-500" />
                <span className="text-xs font-bold">{course.rating.toFixed(1)}</span>
              </div>
            </div>
          )}

          {/* Enrollment Status Badge */}
          {isEnrolled && (
            <div className="absolute top-2 left-2">
              <div className="bg-green-600 text-white px-2 py-1 rounded text-xs font-bold shadow-sm flex items-center">
                <BookOpenIcon className="h-3 w-3 mr-1" />
                {hasProgress ? 'In Progress' : 'Enrolled'}
              </div>
            </div>
          )}

          {/* Expand Image Icon */}
          {course.image_url && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowFullImage(true);
              }}
              className="absolute top-2 right-2 bg-white/90 hover:bg-white text-gray-700 hover:text-blue-600 p-1.5 rounded-full shadow-sm transition-all duration-200 opacity-0 group-hover:opacity-100"
              title="View full image"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
            </button>
          )}

          {/* Price Badge - Hidden on iOS WebView */}
          {!shouldShowIOSWebViewAlert() && (
            <div className="absolute bottom-2 right-2">
              <span className={`px-2 py-1 text-xs font-bold rounded shadow-sm ${
                course.price_status === "Free" || course.pricee === "0"
                  ? "bg-green-600 text-white"
                  : "bg-slate-800 text-white"
              }`}>
                {course.price_status === "Free" || course.pricee === "0"
                  ? "FREE"
                  : `₵${course.pricee || '0'}`}
              </span>
            </div>
          )}
        </div>

        {/* Course Content */}
        <div className="p-3 flex flex-col">
          {/* Title */}
          <h4 className="text-sm font-bold text-gray-900 mb-1 line-clamp-2 min-h-[2.5rem]">
            {course.name}
          </h4>
          
          {/* Rating Display */}
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center">
              <span className="text-sm font-bold text-gray-900 mr-1">
                {course.rating > 0 ? course.rating.toFixed(1) : 'No ratings'}
              </span>
              <div className="flex items-center">
                {[...Array(5)].map((_, index) => (
                  <svg
                    key={index}
                    className={`h-3 w-3 ${
                      course.rating > 0 && index < Math.floor(course.rating)
                        ? 'text-yellow-400'
                        : course.rating > 0 && index < course.rating
                        ? 'text-yellow-400'
                        : 'text-yellow-300'
                    }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            </div>
            <span className="text-xs text-gray-500">
              {course.rating > 0 ? `(${(course as any).reviews_count || 0} reviews)` : '(Be the first to rate)'}
            </span>
          </div>
          
          {/* Author */}
          <div className="mb-2">
            <p className="text-xs text-gray-600 flex items-center">
              <svg className="h-3 w-3 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
              </svg>
              <UserProfileLink 
                userId={course.author.id}
                fallbackName={course.author.name}
                fallbackAvatarUrl={course.author.image_url}
                className="hover:text-primary-600 transition-colors"
              />
            </p>
          </div>

          {/* Course Stats */}
          <div className="flex items-center gap-3 mb-2 text-xs text-gray-600">
            <span className="flex items-center">
              <svg className="h-3 w-3 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
              </svg>
              {course.lessons_count || 0} lessons
            </span>
            <span className="flex items-center">
              <svg className="h-3 w-3 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {course.duration || '0h'}
            </span>
            {classroomsCount && (
              <span className="flex items-center">
                <svg className="h-3 w-3 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
                {classroomsCount} classrooms
              </span>
            )}
          </div>

          {/* Progress Bar for Enrolled Courses */}
          {isEnrolled && courseProgress && (
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>Progress</span>
                <span className="font-medium">
                  {courseProgress.completed ? '100%' : 
                   courseProgress.lastLessonProgress ? `${Math.round(courseProgress.lastLessonProgress)}%` : '0%'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: courseProgress.completed ? '100%' : 
                           courseProgress.lastLessonProgress ? `${courseProgress.lastLessonProgress}%` : '0%'
                  }}
                ></div>
              </div>
            </div>
          )}

          {/* Course Tags */}
          <div className="flex flex-wrap gap-1 mb-3">
            {course.program && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs flex items-center">
                <svg className="h-2.5 w-2.5 mr-1 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                {Array.isArray(course.program) ? course.program.join(', ') : course.program}
              </span>
            )}
            {course.cat_id && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs flex items-center">
                <svg className="h-2.5 w-2.5 mr-1 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM7 7a1 1 0 000 2h6a1 1 0 100-2H7zM7 11a1 1 0 000 2h6a1 1 0 100-2H7z" />
                </svg>
                {course.cat_id}
              </span>
            )}
          </div>

          {/* Action Button */}
          <div className="flex items-center justify-between mt-auto">
            {isFreeCourse ? (
              <button
                onClick={() => window.location.href = `/app/course-learning/${course.id}`}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white text-sm font-bold rounded-lg transition-all duration-200 flex items-center justify-center space-x-3 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                <BookOpenIcon className="h-5 w-5" />
                <span>Start Learning</span>
              </button>
            ) : isEnrolled ? (
              <button
                onClick={() => window.location.href = `/app/course-learning/${course.id}`}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white text-sm font-bold rounded-lg transition-all duration-200 flex items-center justify-center space-x-3 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                <BookOpenIcon className="h-5 w-5" />
                <span>{hasProgress ? 'Continue Learning' : 'Start Learning'}</span>
              </button>
            ) : !shouldShowIOSWebViewAlert() ? (
              <button
                onClick={handleAddToCart}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-bold rounded-lg transition-all duration-200 flex items-center justify-center space-x-3 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                <ShoppingCartIcon className="h-5 w-5" />
                <span>Add to Cart</span>
              </button>
            ) : (
              /* For iOS WebView with paid courses, show a simple "View Details" button */
              <button
                onClick={() => window.location.href = `/courses/${course.id}`}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white text-sm font-bold rounded-lg transition-all duration-200 flex items-center justify-center space-x-3 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                <AcademicCapIcon className="h-5 w-5" />
                <span>View Details</span>
              </button>
            )}
            
            <button
              onClick={handleShareClick}
              className="ml-3 p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
              title="Share course"
            >
              <ShareIcon className="h-5 w-5" />
            </button>
            
            {/* Edit Button - Only show for authors/admins */}
            {showEditButton && onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="ml-3 p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                title="Edit course"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Full Image Modal */}
      {showFullImage && course.image_url && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="relative max-w-4xl max-h-[90vh] w-full h-full">
            <img
              src={course.image_url}
              alt={course.name}
              className="w-full h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setShowFullImage(false)}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-700 hover:text-gray-900 p-2 rounded-full shadow-lg transition-all duration-200"
              title="Close"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogCourseCard;
